import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Joyride,
  EVENTS,
  STATUS,
  EventData,
  Step,
  Placement,
} from "react-joyride";
import {
  toggleOnboardingFlow,
  getAccountSlice,
  getIncompleteOnboardingFlows,
} from "../../redux/slices/accountSlice";
import { OnboardingFlowType } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import OnboardingTourTooltip from "./OnboardingTourTooltip";
import { useOnMount } from "../../hooks/useOnMount";
import {
  getNotificationState,
  notificationActions,
} from "../../redux/slices/notificationSlice";

// Module-level (not React state) - tracks a pending "user abandoned this
// flow" clear per flow type, so it can be cancelled if another step of the
// same flow mounts again shortly after (i.e. the user just navigated to a
// different screen within the same flow, not away from it entirely).
const pendingAbandonTimers: Partial<
  Record<OnboardingFlowType, ReturnType<typeof setTimeout>>
> = {};
const ABANDON_CLEAR_DELAY_MS = 500;

const cancelPendingAbandonTimer = (flowType: OnboardingFlowType) => {
  const pendingTimer = pendingAbandonTimers[flowType];
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    delete pendingAbandonTimers[flowType];
  }
};

export type OnboardingTourStepProps = {
  flowType: OnboardingFlowType;
  target: string;
  content: ReactNode;
  stepNumber: number;
  totalSteps?: number; // omit when the total isn't known yet (shows just "Step N")
  placement?: Placement | "center"; // center places the tooltip in center of screen
  onStepComplete?: () => void;
  mountDelayMs?: number; // delay the mount of the tour until the target is in view
  hideStepIndicator?: boolean; // hides OnboardingTourStepIndicator (for single screen flows)
  skipScroll?: boolean; // prevents joyride from auto-scrolling to the target on mount
};

// Renders a single onboarding tour step, targeting one element on the current page.
const OnboardingTourStep = (props: OnboardingTourStepProps) => {
  const {
    flowType,
    target,
    content,
    stepNumber,
    totalSteps,
    placement,
    onStepComplete,
    mountDelayMs,
    hideStepIndicator,
    skipScroll,
  } = props;
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountSlice);
  const incompleteOnboardingFlows = useSelector(getIncompleteOnboardingFlows);
  const { currentTourStep } = useSelector(getNotificationState);
  const [dismissed, setDismissed] = useState(false);
  const [ready, setReady] = useState(!mountDelayMs);

  const hasIncompleteFlow = incompleteOnboardingFlows.some(
    (flow) => flow.id === flowType
  );

  const alreadySeen =
    !!currentTourStep &&
    currentTourStep.flowType === flowType &&
    stepNumber < currentTourStep.stepNumber;

  useOnMount(() => {
    if (!mountDelayMs) {
      return;
    }
    const timeout = setTimeout(() => setReady(true), mountDelayMs);
    return () => clearTimeout(timeout);
  });

  // Tracks the current step for this flow, and clears it after a delay if unmounted.
  useOnMount(() => {
    if (!hasIncompleteFlow) {
      return;
    }

    cancelPendingAbandonTimer(flowType);

    dispatch(
      notificationActions.setCurrentTourStep({
        flowType,
        stepNumber,
        totalSteps,
        hideStepIndicator: !!hideStepIndicator,
      })
    );

    return () => {
      pendingAbandonTimers[flowType] = setTimeout(() => {
        dispatch(notificationActions.clearCurrentTourStep({ flowType }));
        delete pendingAbandonTimers[flowType];
      }, ABANDON_CLEAR_DELAY_MS);
    };
  });

  const steps: Step[] = useMemo(
    () => [
      {
        target,
        content,
        placement: placement ?? "right",
        skipBeacon: true,
        overlayClickAction: false,
        blockTargetInteraction: true,
        skipScroll: !!skipScroll,
        data: { stepNumber, totalSteps },
      },
    ],
    [target, content, placement, stepNumber, totalSteps, skipScroll]
  );

  const handleEvent = useCallback(
    (data: EventData) => {
      if (data.type !== EVENTS.TOUR_END) {
        return;
      }
      if (data.status === STATUS.SKIPPED) {
        // Skipping ends the whole tour, not just this step
        dispatch(toggleOnboardingFlow(accountData.uid, flowType, true, true));
        dispatch(notificationActions.clearCurrentTourStep({ flowType }));
        cancelPendingAbandonTimer(flowType);
        setDismissed(true);
      } else if (data.status === STATUS.FINISHED) {
        // Only mark the flow complete once the last step is finished
        if (stepNumber === totalSteps) {
          dispatch(
            toggleOnboardingFlow(accountData.uid, flowType, true, false)
          );
          dispatch(notificationActions.clearCurrentTourStep({ flowType }));
          cancelPendingAbandonTimer(flowType);
        }
        setDismissed(true);
        onStepComplete?.();
      }
    },
    [
      accountData.uid,
      dispatch,
      flowType,
      stepNumber,
      totalSteps,
      onStepComplete,
    ]
  );

  const isShowingTooltip =
    hasIncompleteFlow && !dismissed && ready && !alreadySeen;

  // Signals whether this step's real tooltip is actively spotlighted on
  // screen, so the persistent bottom-left indicator can hide itself while
  // the real tooltip is showing rather than overlapping it.
  useEffect(() => {
    if (!hasIncompleteFlow) {
      return;
    }
    dispatch(
      notificationActions.setTourStepVisible({
        flowType,
        stepNumber,
        visible: isShowingTooltip,
      })
    );
  }, [dispatch, flowType, stepNumber, hasIncompleteFlow, isShowingTooltip]);

  if (!isShowingTooltip) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={hasIncompleteFlow && !dismissed}
      continuous
      tooltipComponent={OnboardingTourTooltip}
      onEvent={handleEvent}
      options={{
        overlayColor: "rgba(0, 0, 0, 0.5)",
        spotlightPadding: 8,
        spotlightRadius: 12,
        primaryColor: Colors.BLACK,
        zIndex: 10000,
      }}
    />
  );
};

export default OnboardingTourStep;
